import { v4 as uuidv4 } from 'uuid';

// Initialize FBQ if it doesn't exist
const initializeFBQ = () => {
  if (typeof window !== 'undefined' && !window.fbq) {
    window.fbq = window.fbq || [];
  }
};

// Check if we're in browser environment
const isBrowser = () => typeof window !== 'undefined';

// Get attribution data from localStorage with optional filtering
export const getAttributionData = (requiredParams = null) => {
  if (!isBrowser()) return {};
  
  const allAttributionKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'fbclid'];
  const keysToCheck = requiredParams || allAttributionKeys;
  
  return keysToCheck.reduce((acc, key) => {
    const value = localStorage.getItem(key);
    if (value) acc[key] = value;
  }, {});
};

// Clean object by removing null/undefined values
const cleanEventData = (data) => {
  return Object.fromEntries(
    Object.entries(data).filter(([_, value]) => value !== null && value !== undefined)
  );
};

// Base event tracker with custom event names (like GTM)
export const fbqTrackEvent = (eventName, eventData = {}) => {
  if (!isBrowser() || !window.fbq) {
    console.warn('Meta Pixel: fbq not available');
    return;
  }
  
  const cleanEvent = cleanEventData(eventData);
  
  // Use trackCustom for our custom event names (like GTM does)
  window.fbq('trackCustom', eventName, cleanEvent);
  console.log('Meta Pixel Custom Event tracked:', eventName, cleanEvent);
};

// Create base event with common properties (same as GTM)
const createBaseEvent = (additionalData = {}) => ({
  timestamp: new Date().toISOString(),
  session_id: sessionStorage.getItem('session_id') || uuidv4(),
  ...additionalData
});

// Track page views
export const fbqPageView = (additionalData = {}) => {
  if (!isBrowser() || !window.fbq) {
    console.warn('Meta Pixel: fbq not available');
    return;
  }
  
  const eventData = createBaseEvent({
    ...additionalData,
    ...getAttributionData()
  });
  
  window.fbq('track', 'PageView', eventData);
  console.log('Meta Pixel Page View tracked:', eventData);
};

// Track user login
export const fbqTrackLogin = (user_id, method = 'email') => {
  const eventData = createBaseEvent({
    method,
    user_id,
    ...getAttributionData(['utm_source', 'utm_medium', 'utm_campaign'])
  });
  
  // Use standard Login event
  window.fbq('track', 'Login', eventData);
  console.log('Meta Pixel Login tracked:', eventData);
};

// Track user registration
export const fbqTrackRegistration = (user_id, method = 'email') => {
  const eventData = createBaseEvent({
    method,
    user_id,
    ...getAttributionData(['utm_source', 'utm_medium', 'utm_campaign'])
  });
  
  window.fbq('track', 'CompleteRegistration', eventData);
  console.log('Meta Pixel Registration tracked:', eventData);
};

// Track form submissions
export const fbqTrackFormSubmit = (form_name, form_step = null) => {
  const eventData = createBaseEvent({
    form_name,
    form_step,
    ...getAttributionData(['utm_source', 'utm_medium', 'utm_campaign'])
  });
  
  fbqTrackEvent('form_submit', eventData);
};

// Track button clicks
export const fbqTrackButtonClick = (button_name, section = null) => {
  const eventData = createBaseEvent({
    button_name,
    section
  });
  
  fbqTrackEvent('click', eventData);
};


// NEW CUSTOM EVENTS - SAME STRUCTURE AS GTM

// 1. Track user started registration (Layer 1)
export const fbqTrackUserStartedRegistration = (additionalData = {}) => {
  const eventData = createBaseEvent({
    page_path: '/register',
    ...additionalData,
    ...getAttributionData(['utm_source', 'utm_medium', 'utm_campaign'])
  });
  
  fbqTrackEvent('user_started_registration', eventData);
};

// 2a. Track account type selection (Layer 2a)
export const fbqTrackAccountTypeSelection = (account_type) => {
  const eventData = createBaseEvent({
    account_type, // 'personal' or 'corporate'
    ...getAttributionData(['utm_source', 'utm_medium', 'utm_campaign'])
  });
  
  fbqTrackEvent('user_selected_account_type', eventData);
};

// 2b. Track onboarding step progression (Layer 2b)
export const fbqTrackOnboardingStep = (step_name, step_number, account_type = null) => {
  const eventData = createBaseEvent({
    step_name,
    step_number,
    account_type,
    user_id: window.sessionStorage.getItem("register_user_email") || window.sessionStorage.getItem("register_user_id") || 'unknown',
    ...getAttributionData(['utm_source', 'utm_medium', 'utm_campaign'])
  });
  
  fbqTrackEvent('user_entered_onboarding_step', eventData);
};

// 3a. Track KYC started (Layer 3a)
export const fbqTrackKYCStarted = (account_type) => {
  const eventData = createBaseEvent({
    account_type,
    user_id: window.sessionStorage.getItem("register_user_email") || window.sessionStorage.getItem("register_user_id") || 'unknown',
    ...getAttributionData(['utm_source', 'utm_medium', 'utm_campaign'])
  });
  
  fbqTrackEvent('kyc_started', eventData);
};

// 3b. Track KYC completed (Layer 3b)
export const fbqTrackKYCCompleted = (account_type) => {
  const eventData = createBaseEvent({
    account_type,
    user_id: window.sessionStorage.getItem("register_user_email") || window.sessionStorage.getItem("register_user_id") || 'unknown',
    ...getAttributionData(['utm_source', 'utm_medium', 'utm_campaign'])
  });
  
  fbqTrackEvent('kyc_completed', eventData);
};

// 3c. Track KYC failed (Layer 3c)
export const fbqTrackKYCFailed = (account_type, reason = null) => {
  const eventData = createBaseEvent({
    account_type,
    reason,
    user_id: window.sessionStorage.getItem("register_user_email") || window.sessionStorage.getItem("register_user_id") || 'unknown',
    ...getAttributionData(['utm_source', 'utm_medium', 'utm_campaign'])
  });
  
  fbqTrackEvent('kyc_failed', eventData);
};

// 6. Track card created (Layer 6)
export const fbqTrackCardCreated = (additionalData = {}) => {
  const eventData = createBaseEvent({
    ...additionalData,
    user_id: window.sessionStorage.getItem("register_user_email") || window.sessionStorage.getItem("register_user_id") || 'unknown',
    ...getAttributionData(['utm_source', 'utm_medium', 'utm_campaign'])
  });
  
  fbqTrackEvent('card_created', eventData);
};

// 7. Track first transaction (Layer 7)
export const fbqTrackFirstTransaction = (additionalData = {}) => {
  const eventData = createBaseEvent({
    ...additionalData,
    user_id: window.sessionStorage.getItem("register_user_email") || window.sessionStorage.getItem("register_user_id") || 'unknown',
    ...getAttributionData(['utm_source', 'utm_medium', 'utm_campaign'])
  });
  
  fbqTrackEvent('first_transaction', eventData);
};

// 8. Track account topup (Layer 8)
export const fbqTrackAccountTopup = (additionalData = {}) => {
  const eventData = createBaseEvent({
    ...additionalData,
    user_id: window.sessionStorage.getItem("register_user_email") || window.sessionStorage.getItem("register_user_id") || 'unknown',
    ...getAttributionData(['utm_source', 'utm_medium', 'utm_campaign'])
  });
  
  fbqTrackEvent('account_topup', eventData);
};

// Generic custom event tracker
export const fbqTrackCustomEvent = (eventName, eventData = {}) => {
  const customEventData = createBaseEvent(eventData);
  fbqTrackEvent(eventName, customEventData);
};

// Initialize FBQ on module load
initializeFBQ();